import React, { useEffect, useState } from 'react';
import {
  ShoppingCart, Search, CheckCircle2, Clock, XCircle, Package, Building2, Calendar,
  Grid3x3, List as ListIcon, Table as TableIcon, Shield, X, Copy, FileText, Truck, Euro,
  Check, Send, Trash2, Download
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api, { API_BASE_URL } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { useAlert } from '../../components/AlertProvider';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

interface PurchaseOrder {
  id: number;
  client_id: number;
  order_number: string;
  sales_point_id?: number;
  status: 'pending' | 'confirmed' | 'shipped' | 'delivered' | 'enregistrer' | 'invoiced' | 'paid' | 'cancelled';
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  credit_note_amount?: number;
  currency: string;
  billing_address_id?: number;
  shipping_address_id?: number;
  delivery_address?: string;
  delivery_city?: string;
  delivery_postal_code?: string;
  delivery_country?: string;
  notes?: string;
  admin_notes?: string;
  pdf_url?: string;
  created_at: string;
  updated_at: string;
  confirmed_at?: string;
  shipped_at?: string;
  delivered_at?: string;
  paid_at?: string;
  paid_by_admin_id?: number;
  enregistrer_at?: string;
  enregistrer_by_admin_id?: number;
  client?: {
    id: number;
    company_name: string;
    email: string;
    admin_id?: number;
    admin?: {
      id: number;
      email: string;
      first_name?: string;
      last_name?: string;
    };
  };
  items?: Array<{
    id: number;
    consumable_id: number;
    quantity: number;
    unit_price: number;
    consumable?: {
      title: string;
      unit: string;
    };
  }>;
  invoice?: {
    id: number;
    status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  };
  items_count?: number;
}

const statusConfig = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/30 dark:text-yellow-300', icon: Clock },
  confirmed: { label: 'Confirmé', color: 'bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300', icon: CheckCircle2 },
  shipped: { label: 'Expédié', color: 'bg-purple-100 text-purple-700 dark:bg-purple-950/30 dark:text-purple-300', icon: Package },
  delivered: { label: 'Livré', color: 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300', icon: CheckCircle2 },
  enregistrer: { label: 'Enregistré', color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/30 dark:text-indigo-300', icon: FileText },
  invoiced: { label: 'Facturé', color: 'bg-amber-100 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300', icon: FileText },
  paid: { label: 'Payé', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300', icon: CheckCircle2 },
  cancelled: { label: 'Annulées', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300', icon: XCircle },
} as const;

const formatDate = (value?: string): string => {
  if (!value) return '';

  // Handle YYYY-MM-DD without timezone shifting
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('fr-FR');
  }

  return new Date(value).toLocaleDateString('fr-FR');
};

const getStatusDateInfo = (order: PurchaseOrder): { label: string; value: string } | null => {
  let label: string;
  let dateValue: string | undefined;

  switch (order.status) {
    case 'paid':
      label = 'Date de paiement';
      dateValue = order.paid_at || order.enregistrer_at || order.updated_at;
      break;
    case 'invoiced':
      label = 'Date de facturation';
      dateValue = order.updated_at;
      break;
    case 'confirmed':
      label = 'Date de confirmation';
      dateValue = order.confirmed_at || order.updated_at;
      break;
    case 'shipped':
      label = "Date d'expédition";
      dateValue = order.shipped_at || order.updated_at;
      break;
    case 'delivered':
      label = 'Date de livraison';
      dateValue = order.delivered_at || order.updated_at;
      break;
    case 'enregistrer':
      label = "Date d'enregistrement";
      dateValue = order.enregistrer_at || order.updated_at;
      break;
    case 'cancelled':
      label = "Date d'annulation";
      dateValue = order.updated_at;
      break;
    case 'pending':
    default:
      label = 'Dernière mise à jour';
      dateValue = order.updated_at;
      break;
  }

  if (!dateValue) return null;
  const formatted = formatDate(dateValue);
  if (!formatted) return null;
  return { label, value: formatted };
};

const getNetHt = (order: PurchaseOrder): number => {
  return order.subtotal - (order.credit_note_amount || 0);
};

const PurchaseOrdersTailwind: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError, showInfo } = useAlert();
  const isManager: boolean = ['super_admin', 'admin', 'apporteur'].includes(user?.role || '');
  const isApporteur = user?.role === 'apporteur';

  const [items, setItems] = useState<PurchaseOrder[]>([]);
  const [apporteurs, setApporteurs] = useState<Array<{ id: number; email: string; first_name: string | null; last_name: string | null }>>([]);
  const [busy, setBusy] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'table'>(() => window.innerWidth < 640 ? 'grid' : 'table');
  const [apporteurFilter, setApporteurFilter] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateFilter, setDateFilter] = useState<string>('');
  const [amountMin, setAmountMin] = useState<string>('');
  const [amountMax, setAmountMax] = useState<string>('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState<boolean>(false);
  const [expandedOrder, setExpandedOrder] = useState<number | null>(null);
  const [pdfType, setPdfType] = useState<'bdc' | 'bdl' | 'invoice' | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loadingPdf, setLoadingPdf] = useState<boolean>(false);

  useEffect(() => {
    if (isApporteur && user?.id) {
      setApporteurFilter(user.id);
    }
  }, [isApporteur, user?.id]);

  const [showEnregistrerPopup, setShowEnregistrerPopup] = useState<boolean>(false);
  const [enregistrerOrderId, setEnregistrerOrderId] = useState<number | null>(null);
  const [enregistrerDate, setEnregistrerDate] = useState<string>('');
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; orderId: number | null }>({ open: false, orderId: null });
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [actionLoading, setActionLoading] = useState<{ id: number; action: string } | null>(null);

  const loadApporteurs = async (): Promise<void> => {
    try {
      const res = await api.get('/users/admins');
      const list = Array.isArray(res.data) ? res.data : [];
      setApporteurs(list);
    } catch (e) {
      console.error('Load apporteurs error:', e);
    }
  };

  const deleteOrder = (orderId: number): void => {
    setDeleteDialog({ open: true, orderId });
  };

  const handleConfirmDelete = async (): Promise<void> => {
    if (!deleteDialog.orderId) return;

    setIsDeleting(true);
    try {
      await api.delete(`/purchase-orders/${deleteDialog.orderId}`);
      showSuccess('Commande supprimée avec succès');
      setDeleteDialog({ open: false, orderId: null });
      await load();
    } catch (error: any) {
      console.error('Delete order error:', error);
      showError(error?.response?.data?.detail || 'Une erreur est survenue lors de la suppression');
    } finally {
      setIsDeleting(false);
    }
  };

  const load = async (): Promise<void> => {
    setBusy(true);
    try {
      const res = await api.get('/purchase-orders');
      const orders = Array.isArray(res.data) ? res.data : [];

      console.log('Orders received:', orders.length);

      setItems(orders);
    } catch (error: any) {
      console.error('Error loading orders:', error);
      showError('Impossible de charger les commandes');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!isApporteur) {
      void loadApporteurs();
    } else {
      setApporteurs([]);
    }
  }, [isApporteur]);
  useEffect(() => { void load(); }, []);

  // Les filtres sont appliqués côté frontend, pas besoin de recharger les données

  const getApporteurName = (item: PurchaseOrder): string => {
    // First try to get from client.admin (new backend response)
    if (item.client?.admin) {
      const admin = item.client.admin;
      if (admin.first_name && admin.last_name) {
        return `${admin.first_name} ${admin.last_name}`;
      }
      return admin.email;
    }
    // Fallback to apporteurs list lookup
    if (item.client?.admin_id) {
      const apporteur = apporteurs.find(a => a.id === item.client!.admin_id);
      if (apporteur) {
        if (apporteur.first_name && apporteur.last_name) {
          return `${apporteur.first_name} ${apporteur.last_name}`;
        }
        return apporteur.email;
      }
      return `Apporteur #${item.client.admin_id}`;
    }
    return 'Non renseigné';
  };

  const getApporteurInitials = (item: PurchaseOrder): string => {
    // First try to get from client.admin (new backend response)
    if (item.client?.admin) {
      const admin = item.client.admin;
      if (admin.first_name && admin.last_name) {
        return `${admin.first_name[0]}${admin.last_name[0]}`.toUpperCase();
      }
      return admin.email[0].toUpperCase();
    }
    // Fallback to apporteurs list lookup
    if (item.client?.admin_id) {
      const apporteur = apporteurs.find(a => a.id === item.client!.admin_id);
      if (apporteur) {
        if (apporteur.first_name && apporteur.last_name) {
          return `${apporteur.first_name[0]}${apporteur.last_name[0]}`.toUpperCase();
        }
        return apporteur.email[0].toUpperCase();
      }
    }
    return 'A';
  };

  const handleViewPdf = async (orderId: number, type: 'bdc' | 'bdl' | 'invoice'): Promise<void> => {
    if (expandedOrder === orderId && pdfType === type) {
      closePdfViewer();
      return;
    }

    setExpandedOrder(orderId);
    setPdfType(type);
    setPdfUrl(null);
    setLoadingPdf(true);

    const token = useAuthStore.getState().token;
    if (!token) { setLoadingPdf(false); return; }

    try {
      if (type === 'bdc') {
        setPdfUrl(`${API_BASE_URL}/purchase-orders/${orderId}/pdf?token=${encodeURIComponent(token)}`);
      } else if (type === 'bdl') {
        setPdfUrl(`${API_BASE_URL}/purchase-orders/${orderId}/delivery-note/pdf?token=${encodeURIComponent(token)}`);
      } else if (type === 'invoice') {
        let invoiceResponse = await api.get(`/invoices/by-purchase-order/${orderId}`, {
          validateStatus: (s) => s === 200 || s === 204,
        });

        let invoiceId: number | null = null;
        if (invoiceResponse.status === 204 || !invoiceResponse.data?.id) {
          showInfo('Génération de la facture en cours...');
          const createResponse = await api.post('/invoices/from-purchase-order', {
            purchase_order_id: orderId,
            percentage: 100,
            due_days: 30,
          });
          invoiceId = createResponse.data?.id;
          showSuccess('Facture créée avec succès');
        } else {
          invoiceId = invoiceResponse.data.id;
        }

        if (!invoiceId) throw new Error('Impossible de créer ou récupérer la facture');
        setPdfUrl(`${API_BASE_URL}/invoices/${invoiceId}/pdf?token=${encodeURIComponent(token)}`);
      }
    } catch (error: any) {
      console.error('[PDF] Error loading PDF:', error);
      showError(error?.response?.data?.detail || error.message || 'Erreur lors du chargement du PDF');
      setExpandedOrder(null);
      setPdfType(null);
    } finally {
      setLoadingPdf(false);
    }
  };

  const closePdfViewer = (): void => {
    setExpandedOrder(null);
    setPdfType(null);
    setPdfUrl(null);
  };

  const handleQuickStatusChange = async (orderId: number, newStatus: string): Promise<void> => {
    setActionLoading({ id: orderId, action: newStatus });
    try {
      await api.patch(`/purchase-orders/${orderId}`, { status: newStatus });
      await load();

      switch (newStatus) {
        case 'confirmed':
          showSuccess('Commande validée');
          break;
        case 'shipped':
          showSuccess('Commande marquée comme expédiée');
          break;
        case 'delivered':
          showSuccess('Commande marquée comme livrée');
          break;
        case 'invoiced':
          showSuccess('Commande facturée');
          break;
        case 'paid':
          showSuccess('Commande marquée comme payée');
          break;
        case 'cancelled':
          showSuccess('Commande refusée');
          break;
        default:
          showSuccess('Statut mis à jour');
      }
    } catch (error: any) {
      console.error('Erreur changement statut:', error);
      showError(error.response?.data?.detail || "Erreur lors de la mise à jour du statut");
    } finally {
      setActionLoading(null);
    }
  };


  const handleMarkEnregistrer = (orderId: number) => {
    setEnregistrerOrderId(orderId);
    setShowEnregistrerPopup(true);
    // Set default date to today
    const today = new Date().toISOString().split('T')[0];
    setEnregistrerDate(today);
  };

  const handleConfirmEnregistrer = async () => {
    if (!enregistrerOrderId || !enregistrerDate) return;

    setActionLoading({ id: enregistrerOrderId, action: 'paid' });
    try {
      await api.patch(`/purchase-orders/${enregistrerOrderId}`, {
        status: 'paid',
        paid_at: enregistrerDate
      });

      showSuccess(`Paiement enregistré avec succès le ${enregistrerDate}`);
      setShowEnregistrerPopup(false);
      setEnregistrerOrderId(null);
      setEnregistrerDate('');
      await load();
    } catch (error: any) {
      showError(error.response?.data?.detail || "Impossible d'enregistrer le paiement");
    } finally {
      setActionLoading(null);
    }
  };

  const clearFilters = (): void => {
    setSearch('');
    setApporteurFilter(isApporteur ? (user?.id ?? null) : null);
    setStatusFilter('');
    setDateFilter('');
    setAmountMin('');
    setAmountMax('');
    setShowAdvancedFilters(false);
  };

  const stats = {
    total: items.length,
    pending: items.filter(i => i.status === 'pending').length,
    confirmed: items.filter(i => i.status === 'confirmed').length,
    shipped: items.filter(i => i.status === 'shipped').length,
    delivered: items.filter(i => i.status === 'delivered').length,
    invoiced: items.filter(i => i.status === 'invoiced').length,
    paid: items.filter(i => i.status === 'paid').length,
  };

  const getCurrentTotalHT = () => {
    const targetItems = statusFilter
      ? items.filter(i => i.status === statusFilter)
      : items.filter(i => i.status !== 'cancelled');
    return Math.round(targetItems.reduce((sum, i) => sum + getNetHt(i), 0));
  };

  // Filtrage côté frontend
  const filtered = items.filter(item => {
    // Filtre par recherche (nom client ou référence commande)
    if (search) {
      const searchLower = search.toLowerCase();
      const matchOrderNumber = item.order_number?.toLowerCase().includes(searchLower);
      const matchClientName = item.client?.company_name?.toLowerCase().includes(searchLower);
      if (!matchOrderNumber && !matchClientName) return false;
    }

    // Filtre par apporteur
    if (apporteurFilter && item.client?.admin_id !== apporteurFilter) {
      return false;
    }

    // Filtre par statut
    if (statusFilter && item.status !== statusFilter) {
      return false;
    }

    // Filtre par date
    if (dateFilter) {
      const orderDate = new Date(item.created_at);
      const now = new Date();

      switch (dateFilter) {
        case 'today':
          if (orderDate.toDateString() !== now.toDateString()) return false;
          break;
        case 'week':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          if (orderDate < weekAgo) return false;
          break;
        case 'month':
          if (orderDate.getMonth() !== now.getMonth() || orderDate.getFullYear() !== now.getFullYear()) return false;
          break;
        case 'quarter':
          const currentQuarter = Math.floor(now.getMonth() / 3);
          const orderQuarter = Math.floor(orderDate.getMonth() / 3);
          if (orderQuarter !== currentQuarter || orderDate.getFullYear() !== now.getFullYear()) return false;
          break;
        case 'year':
          if (orderDate.getFullYear() !== now.getFullYear()) return false;
          break;
      }
    }

    // Filtre par montant minimum
    if (amountMin && getNetHt(item) < parseFloat(amountMin)) {
      return false;
    }

    // Filtre par montant maximum
    if (amountMax && getNetHt(item) > parseFloat(amountMax)) {
      return false;
    }

    return true;
  });

  return (
    <div className="h-full overflow-auto sm:overflow-hidden sm:flex sm:flex-col bg-gradient-to-br from-slate-50 via-blue-50/20 to-slate-50 dark:from-slate-950 dark:via-blue-950/10 dark:to-slate-950">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:gap-4 sm:flex-row sm:items-center sm:justify-between px-3 sm:px-6 py-2 sm:py-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-700/50 shadow-lg">
        <div className="flex items-center gap-2 sm:gap-4">
          <div className="w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-br from-cyan-500 via-blue-500 to-cyan-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg">
            <ShoppingCart className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
          </div>
          <div>
            <h1 className="text-base sm:text-2xl font-bold text-slate-900 dark:text-slate-100">Commandes client</h1>
          </div>
        </div>
        {isManager && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/achat/orders/new')}
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200"
            >
              <ShoppingCart className="h-4 w-4 sm:h-5 sm:w-5" />
              Créer une commande
            </button>
          </div>
        )}
      </div>

      {/* Stats Cards - Mobile: compact 4-col grid (< md) */}
      <div className="md:hidden px-3 py-2">
        <div className="grid grid-cols-4 gap-1.5">
          <button onClick={() => setStatusFilter('')} className={`flex flex-col items-center gap-0.5 rounded-lg border py-1.5 transition-all ${!statusFilter ? 'border-blue-400 bg-blue-50 dark:bg-blue-950/30 shadow-sm' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'}`}>
            <ShoppingCart className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
            <span className="text-xs font-bold text-slate-800 dark:text-slate-100">{stats.total}</span>
            <span className="text-[8px] uppercase font-semibold text-slate-400">Total</span>
          </button>
          <button onClick={() => setStatusFilter('pending')} className={`flex flex-col items-center gap-0.5 rounded-lg border py-1.5 transition-all ${statusFilter === 'pending' ? 'border-yellow-400 bg-yellow-50 dark:bg-yellow-950/30 shadow-sm' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'}`}>
            <Clock className="h-3.5 w-3.5 text-yellow-600 dark:text-yellow-400" />
            <span className="text-xs font-bold text-yellow-600 dark:text-yellow-400">{stats.pending}</span>
            <span className="text-[8px] uppercase font-semibold text-slate-400">Attente</span>
          </button>
          <button onClick={() => setStatusFilter('confirmed')} className={`flex flex-col items-center gap-0.5 rounded-lg border py-1.5 transition-all ${statusFilter === 'confirmed' ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-950/30 shadow-sm' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'}`}>
            <CheckCircle2 className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
            <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400">{stats.confirmed}</span>
            <span className="text-[8px] uppercase font-semibold text-slate-400">Confirmé</span>
          </button>
          <button onClick={() => setStatusFilter('shipped')} className={`flex flex-col items-center gap-0.5 rounded-lg border py-1.5 transition-all ${statusFilter === 'shipped' ? 'border-purple-400 bg-purple-50 dark:bg-purple-950/30 shadow-sm' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'}`}>
            <Package className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
            <span className="text-xs font-bold text-purple-600 dark:text-purple-400">{stats.shipped}</span>
            <span className="text-[8px] uppercase font-semibold text-slate-400">Expédié</span>
          </button>
          <button onClick={() => setStatusFilter('delivered')} className={`flex flex-col items-center gap-0.5 rounded-lg border py-1.5 transition-all ${statusFilter === 'delivered' ? 'border-green-400 bg-green-50 dark:bg-green-950/30 shadow-sm' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'}`}>
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
            <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">{stats.delivered}</span>
            <span className="text-[8px] uppercase font-semibold text-slate-400">Livré</span>
          </button>
          <button onClick={() => setStatusFilter('invoiced')} className={`flex flex-col items-center gap-0.5 rounded-lg border py-1.5 transition-all ${statusFilter === 'invoiced' ? 'border-orange-400 bg-orange-50 dark:bg-orange-950/30 shadow-sm' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'}`}>
            <FileText className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />
            <span className="text-xs font-bold text-orange-600 dark:text-orange-400">{stats.invoiced}</span>
            <span className="text-[8px] uppercase font-semibold text-slate-400">Facturé</span>
          </button>
          <button onClick={() => setStatusFilter('paid')} className={`flex flex-col items-center gap-0.5 rounded-lg border py-1.5 transition-all ${statusFilter === 'paid' ? 'border-teal-400 bg-teal-50 dark:bg-teal-950/30 shadow-sm' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'}`}>
            <Euro className="h-3.5 w-3.5 text-teal-600 dark:text-teal-400" />
            <span className="text-xs font-bold text-teal-600 dark:text-teal-400">{stats.paid}</span>
            <span className="text-[8px] uppercase font-semibold text-slate-400">Payé</span>
          </button>
          <div className="flex flex-col items-center justify-center rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 py-1.5">
            <span className="text-[10px] font-bold text-slate-800 dark:text-slate-200">{getCurrentTotalHT().toLocaleString('fr-FR')} €</span>
            <span className="text-[8px] uppercase font-semibold text-slate-400">HT</span>
          </div>
        </div>
      </div>

      {/* Stats Cards - Desktop (md+) */}
      <div className="hidden md:block px-6 py-2">
        <div className="grid grid-cols-4 xl:grid-cols-8 gap-2">
          {/* Total */}
          <button
            onClick={() => setStatusFilter('')}
            className={`group relative overflow-hidden rounded-xl border ${!statusFilter
              ? 'border-blue-400 shadow-md bg-white/90 dark:bg-slate-900/90'
              : 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-blue-200/80'} px-2 py-1.5 text-left transition-all duration-200`}
          >
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-blue-50/70 to-indigo-50/70 dark:from-blue-950/20 dark:to-indigo-950/20 pointer-events-none transition-opacity duration-200" />
            <div className="relative flex items-center justify-between h-full">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-300 flex-shrink-0">
                  <ShoppingCart className="h-3.5 w-3.5" />
                </div>
                <div className="flex flex-col"><span className="text-[10px] font-bold uppercase text-slate-500 dark:text-slate-400">Total</span></div>
              </div>
              <div className="text-lg font-bold text-slate-800 dark:text-slate-100">{stats.total}</div>
            </div>
          </button>

          {/* En attente */}
          <button
            onClick={() => setStatusFilter('pending')}
            className={`group relative overflow-hidden rounded-xl border ${statusFilter === 'pending'
              ? 'border-yellow-400 shadow-md bg-white/90 dark:bg-slate-900/90'
              : 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-yellow-200/80'} px-2 py-1.5 text-left transition-all duration-200`}
          >
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-yellow-50/70 to-amber-50/70 dark:from-yellow-950/20 dark:to-amber-950/20 pointer-events-none transition-opacity duration-200" />
            <div className="relative flex items-center justify-between h-full">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-yellow-50 text-yellow-600 dark:bg-yellow-950/40 dark:text-yellow-300 flex-shrink-0">
                  <Clock className="h-3.5 w-3.5" />
                </div>
                <div className="flex flex-col"><span className="text-[10px] font-bold uppercase text-slate-500 dark:text-slate-400">Attente</span></div>
              </div>
              <div className="text-lg font-bold text-yellow-600 dark:text-yellow-400">{stats.pending}</div>
            </div>
          </button>

          {/* Confirmé */}
          <button
            onClick={() => setStatusFilter('confirmed')}
            className={`group relative overflow-hidden rounded-xl border ${statusFilter === 'confirmed'
              ? 'border-indigo-400 shadow-md bg-white/90 dark:bg-slate-900/90'
              : 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-indigo-200/80'} px-2 py-1.5 text-left transition-all duration-200`}
          >
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-indigo-50/70 to-blue-50/70 dark:from-indigo-950/20 dark:to-blue-950/20 pointer-events-none transition-opacity duration-200" />
            <div className="relative flex items-center justify-between h-full">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-50 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-300 flex-shrink-0">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                </div>
                <div className="flex flex-col"><span className="text-[10px] font-bold uppercase text-slate-500 dark:text-slate-400">Confirmé</span></div>
              </div>
              <div className="text-lg font-bold text-indigo-600 dark:text-indigo-400">{stats.confirmed}</div>
            </div>
          </button>

          {/* Expédié */}
          <button
            onClick={() => setStatusFilter('shipped')}
            className={`group relative overflow-hidden rounded-xl border ${statusFilter === 'shipped'
              ? 'border-purple-400 shadow-md bg-white/90 dark:bg-slate-900/90'
              : 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-purple-200/80'} px-2 py-1.5 text-left transition-all duration-200`}
          >
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-purple-50/70 to-fuchsia-50/70 dark:from-purple-950/20 dark:to-fuchsia-950/20 pointer-events-none transition-opacity duration-200" />
            <div className="relative flex items-center justify-between h-full">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-purple-50 text-purple-600 dark:bg-purple-950/40 dark:text-purple-300 flex-shrink-0">
                  <Package className="h-3.5 w-3.5" />
                </div>
                <div className="flex flex-col"><span className="text-[10px] font-bold uppercase text-slate-500 dark:text-slate-400">Expédié</span></div>
              </div>
              <div className="text-lg font-bold text-purple-600 dark:text-purple-400">{stats.shipped}</div>
            </div>
          </button>

          {/* Livré */}
          <button
            onClick={() => setStatusFilter('delivered')}
            className={`group relative overflow-hidden rounded-xl border ${statusFilter === 'delivered'
              ? 'border-green-400 shadow-md bg-white/90 dark:bg-slate-900/90'
              : 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-green-200/80'} px-2 py-1.5 text-left transition-all duration-200`}
          >
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-emerald-50/70 to-teal-50/70 dark:from-emerald-950/20 dark:to-teal-950/20 pointer-events-none transition-opacity duration-200" />
            <div className="relative flex items-center justify-between h-full">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300 flex-shrink-0">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                </div>
                <div className="flex flex-col"><span className="text-[10px] font-bold uppercase text-slate-500 dark:text-slate-400">Livré</span></div>
              </div>
              <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{stats.delivered}</div>
            </div>
          </button>

          {/* Facturé */}
          <button
            onClick={() => setStatusFilter('invoiced')}
            className={`group relative overflow-hidden rounded-xl border ${statusFilter === 'invoiced'
              ? 'border-orange-400 shadow-md bg-white/90 dark:bg-slate-900/90'
              : 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-orange-200/80'} px-2 py-1.5 text-left transition-all duration-200`}
          >
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-orange-50/70 to-amber-50/70 dark:from-orange-950/20 dark:to-amber-950/20 pointer-events-none transition-opacity duration-200" />
            <div className="relative flex items-center justify-between h-full">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-orange-50 text-orange-600 dark:bg-orange-950/40 dark:text-orange-300 flex-shrink-0">
                  <FileText className="h-3.5 w-3.5" />
                </div>
                <div className="flex flex-col"><span className="text-[10px] font-bold uppercase text-slate-500 dark:text-slate-400">Facturé</span></div>
              </div>
              <div className="text-lg font-bold text-orange-600 dark:text-orange-400">{stats.invoiced}</div>
            </div>
          </button>

          {/* Payé */}
          <button
            onClick={() => setStatusFilter('paid')}
            className={`group relative overflow-hidden rounded-xl border ${statusFilter === 'paid'
              ? 'border-teal-400 shadow-md bg-white/90 dark:bg-slate-900/90'
              : 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-teal-200/80'} px-2 py-1.5 text-left transition-all duration-200`}
          >
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-teal-50/70 to-emerald-50/70 dark:from-teal-950/20 dark:to-emerald-950/20 pointer-events-none transition-opacity duration-200" />
            <div className="relative flex items-center justify-between h-full">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-teal-50 text-teal-600 dark:bg-teal-950/40 dark:text-teal-300 flex-shrink-0">
                  <Euro className="h-3.5 w-3.5" />
                </div>
                <div className="flex flex-col"><span className="text-[10px] font-bold uppercase text-slate-500 dark:text-slate-400">Payé</span></div>
              </div>
              <div className="text-lg font-bold text-teal-600 dark:text-teal-400">{stats.paid}</div>
            </div>
          </button>

          {/* Total HT Counter */}
          <div className="relative overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 px-2 py-1.5 text-left flex flex-col justify-center">
            <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-950 pointer-events-none" />
            <div className="relative w-full">
              <div className="text-[10px] font-semibold uppercase text-slate-500 dark:text-slate-400 truncate leading-none mb-1">
                Total {statusFilter ? (statusConfig[statusFilter as keyof typeof statusConfig]?.label || statusFilter) : 'Global'} HT
              </div>
              <div className="text-lg font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-slate-100 dark:to-slate-300 bg-clip-text text-transparent truncate leading-none">
                {getCurrentTotalHT().toLocaleString('fr-FR')} € HT
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="px-3 sm:px-6 py-3">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3">
          <div className="relative w-full sm:w-auto sm:flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par nom client, référence commande..."
              className="w-full h-10 pl-10 pr-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          {/* Filtre Apporteur */}
          {isManager && !isApporteur && apporteurs.length > 0 && (
            <div className="relative flex-1 sm:flex-none">
              <select
                value={apporteurFilter || ''}
                onChange={(e) => setApporteurFilter(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full sm:w-auto h-10 pl-10 pr-8 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 appearance-none cursor-pointer"
              >
                <option value="">Tous les apporteurs</option>
                {apporteurs.map(apporteur => (
                  <option key={apporteur.id} value={apporteur.id}>
                    {apporteur.first_name && apporteur.last_name ? `${apporteur.first_name} ${apporteur.last_name}` : apporteur.email}
                  </option>
                ))}
              </select>
              <Shield className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              {apporteurFilter && (
                <button
                  onClick={() => setApporteurFilter(null)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                >
                  <X className="h-3 w-3 text-slate-500" />
                </button>
              )}
            </div>
          )}

          {/* Filtre Statut */}
          <div className="relative flex-1 sm:flex-none">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full sm:w-auto h-10 pl-3 pr-8 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 appearance-none cursor-pointer"
            >
              <option value="">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="confirmed">Confirmé</option>
              <option value="shipped">Expédié</option>
              <option value="delivered">Livré</option>
              <option value="enregistrer">Enregistré</option>
              <option value="invoiced">Facturé</option>
              <option value="paid">Payé</option>
              <option value="cancelled">Annulé</option>
            </select>
            {statusFilter && (
              <button
                onClick={() => setStatusFilter('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
              >
                <X className="h-3 w-3 text-slate-500" />
              </button>
            )}
          </div>

          {/* Boutons filtres avancés et vue */}
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`h-10 px-3 rounded-lg border transition-colors text-sm whitespace-nowrap ${showAdvancedFilters
              ? 'bg-orange-100 border-orange-300 text-orange-700 dark:bg-orange-950/30 dark:border-orange-600 dark:text-orange-300'
              : 'border-slate-300 dark:border-slate-600 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
          >
            Filtres avancés
          </button>

          <button
            onClick={clearFilters}
            className="h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-sm whitespace-nowrap"
          >
            Réinitialiser
          </button>

          <div className="flex items-center gap-1 border border-slate-300 dark:border-slate-600 rounded-lg p-1">
            <button onClick={() => setViewMode('grid')} className={`p-2 rounded ${viewMode === 'grid' ? 'bg-orange-100 dark:bg-orange-950/30 text-orange-700' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
              <Grid3x3 className="h-4 w-4" />
            </button>
            <button onClick={() => setViewMode('list')} className={`p-2 rounded ${viewMode === 'list' ? 'bg-orange-100 dark:bg-orange-950/30 text-orange-700' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
              <ListIcon className="h-4 w-4" />
            </button>
            <button onClick={() => setViewMode('table')} className={`hidden sm:block p-2 rounded ${viewMode === 'table' ? 'bg-orange-100 dark:bg-orange-950/30 text-orange-700' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
              <TableIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Filtres avancés */}
        {showAdvancedFilters && (
          <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Filtre par date */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Période</label>
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">Toutes les périodes</option>
                  <option value="today">Aujourd'hui</option>
                  <option value="week">Cette semaine</option>
                  <option value="month">Ce mois</option>
                  <option value="quarter">Ce trimestre</option>
                  <option value="year">Cette année</option>
                </select>
              </div>

              {/* Filtre montant minimum */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Montant minimum (€ HT)</label>
                <input
                  type="number"
                  value={amountMin}
                  onChange={(e) => setAmountMin(e.target.value)}
                  placeholder="0"
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {/* Filtre montant maximum */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Montant maximum (€ HT)</label>
                <input
                  type="number"
                  value={amountMax}
                  onChange={(e) => setAmountMax(e.target.value)}
                  placeholder="∞"
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="sm:flex-1 sm:overflow-auto px-3 pb-4 sm:px-6 sm:pb-6 sm:min-h-0">
        {busy ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400">
            <ShoppingCart className="h-16 w-16 mb-4 opacity-50" />
            <p className="text-lg font-medium">Aucune commande trouvée</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-4">
            {filtered.map((item, idx) => {
              const status = statusConfig[item.status] || statusConfig.pending;
              const StatusIcon = status.icon;
              const statusDateInfo = getStatusDateInfo(item);
              return (
                <div
                  key={item.id}
                  onClick={() => navigate(`/achat/orders/${item.id}`)}
                  className="group relative p-3 sm:p-5 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:shadow-xl hover:border-orange-300 dark:hover:border-orange-700 hover:scale-[1.02] transition-all duration-300 ease-out animate-in fade-in slide-in-from-bottom-4 cursor-pointer"
                  style={{ animationDelay: `${idx * 50}ms`, animationFillMode: 'backwards' }}
                >
                  <div className="flex items-start justify-between gap-2 sm:gap-3 mb-2 sm:mb-4">
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${(statusConfig[item.status] || statusConfig.pending).color}`}>
                      <StatusIcon className="h-3 w-3" />
                      {(statusConfig[item.status] || statusConfig.pending).label}
                    </div>
                    {statusDateInfo && (
                      <div className="text-right">
                        <div className="text-xs font-semibold text-slate-900 dark:text-slate-100 whitespace-nowrap">
                          {statusDateInfo.label}
                        </div>
                        <div className="text-xs text-slate-600 dark:text-slate-300 whitespace-nowrap">
                          {statusDateInfo.value}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-4">
                    <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-900/30 dark:to-amber-900/30 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                      <ShoppingCart className="h-4 w-4 sm:h-6 sm:w-6 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm sm:text-base font-bold text-slate-900 dark:text-slate-100 truncate">{item.order_number}</h3>
                      <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 truncate">{item.client?.company_name || `Client #${item.client_id}`}</p>
                    </div>
                  </div>
                  <div className="px-1 space-y-1.5 sm:space-y-3 mb-2 sm:mb-4">
                    <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                      <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                        <Calendar className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-emerald-500 dark:text-emerald-400" />
                      </div>
                      <span className="text-slate-600 dark:text-slate-400">{new Date(item.created_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                      <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                        <Package className={`h-3 w-3 sm:h-3.5 sm:w-3.5 ${item.items && item.items.length > 0 ? 'text-emerald-500 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`} />
                      </div>
                      <span className="text-slate-600 dark:text-slate-400">{item.items_count ?? item.items?.length ?? 0} article{(item.items_count ?? item.items?.length ?? 0) > 1 ? 's' : ''}</span>
                    </div>
                    {isManager && (
                      <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                        <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                          <Shield className={`h-3 w-3 sm:h-3.5 sm:w-3.5 ${(item.client?.admin_id || item.client?.admin) ? 'text-emerald-500 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`} />
                        </div>
                        <span className={`truncate ${(item.client?.admin_id || item.client?.admin) ? 'text-slate-600 dark:text-slate-400' : 'text-slate-400 italic'}`}>
                          {getApporteurName(item)}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm font-bold text-orange-600 pt-0.5 sm:pt-1">
                      <div className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center flex-shrink-0"></div>
                      {Math.round(getNetHt(item)).toLocaleString('fr-FR')} € HT
                    </div>
                  </div>

                  {/* Boutons PDF */}
                  <div className="flex flex-wrap gap-1 sm:gap-1.5 mb-2 sm:mb-3">
                    {item.status === 'cancelled' ? (
                      <div className="flex w-full gap-1.5">
                        <button
                          onClick={(e) => { e.stopPropagation(); void handleViewPdf(item.id, 'bdc'); }}
                          className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium rounded-lg transition-all ${expandedOrder === item.id && pdfType === 'bdc'
                            ? 'bg-blue-600 text-white shadow-md'
                            : 'bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-950/50'
                            }`}
                          title="Voir BDC"
                        >
                          <FileText className="h-3.5 w-3.5" />
                          BDC
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); void deleteOrder(item.id); }}
                          className="flex items-center justify-center px-3 py-1.5 text-xs font-medium bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-100 dark:hover:bg-red-950/50 hover:scale-105 transition-all duration-200"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); void handleViewPdf(item.id, 'bdc'); }}
                        className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium rounded-lg transition-all ${expandedOrder === item.id && pdfType === 'bdc'
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-950/50'
                          }`}
                        title="Voir BDC"
                      >
                        <FileText className="h-3.5 w-3.5" />
                        BDC
                      </button>
                    )}

                    {(item.status === 'shipped' || item.status === 'delivered' || item.status === 'invoiced' || item.status === 'enregistrer' || item.status === 'paid') && (
                      <button
                        onClick={(e) => { e.stopPropagation(); void handleViewPdf(item.id, 'bdl'); }}
                        className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium rounded-lg transition-all ${expandedOrder === item.id && pdfType === 'bdl'
                          ? 'bg-purple-600 text-white shadow-md'
                          : 'bg-purple-50 text-purple-700 hover:bg-purple-100 dark:bg-purple-950/30 dark:text-purple-300 dark:hover:bg-purple-950/50'
                          }`}
                        title="Voir BDL"
                      >
                        <Truck className="h-3.5 w-3.5" />
                        BDL
                      </button>
                    )}

                    {(item.status === 'delivered' || item.status === 'invoiced' || item.status === 'paid' || item.status === 'enregistrer') && (!isApporteur || item.invoice) && (
                      <button
                        onClick={(e) => { e.stopPropagation(); void handleViewPdf(item.id, 'invoice'); }}
                        className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium rounded-lg transition-all ${expandedOrder === item.id && pdfType === 'invoice'
                          ? 'bg-green-600 text-white shadow-md'
                          : 'bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-950/30 dark:text-green-300 dark:hover:bg-green-950/50'
                          }`}
                        title="Voir Facture"
                      >
                        <Euro className="h-3.5 w-3.5" />
                        Facture
                      </button>
                    )}

                  </div>

                  {/* Actions Manager */}
                  {isManager && (
                    <div className="flex flex-wrap gap-1 sm:gap-1.5">
                      {/* Changement de statut rapide */}
                      {item.status === 'pending' && !isApporteur && (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); void handleQuickStatusChange(item.id, 'confirmed'); }}
                            disabled={actionLoading?.id === item.id}
                            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-950/30 dark:text-green-300 dark:hover:bg-green-950/50 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Valider la commande"
                          >
                            {actionLoading?.id === item.id && actionLoading?.action === 'confirmed' ? (
                              <div className="h-3.5 w-3.5 border-2 border-green-700 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Check className="h-3.5 w-3.5" />
                            )}
                            Valider
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); void handleQuickStatusChange(item.id, 'cancelled'); }}
                            disabled={actionLoading?.id === item.id}
                            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-950/30 dark:text-red-300 dark:hover:bg-red-950/50 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Refuser la commande"
                          >
                            {actionLoading?.id === item.id && actionLoading?.action === 'cancelled' ? (
                              <div className="h-3.5 w-3.5 border-2 border-red-700 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <XCircle className="h-3.5 w-3.5" />
                            )}
                            Refuser
                          </button>
                        </>
                      )}
                      {item.status === 'confirmed' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); void handleQuickStatusChange(item.id, 'shipped'); }}
                          disabled={actionLoading?.id === item.id}
                          className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium bg-purple-50 text-purple-700 hover:bg-purple-100 dark:bg-purple-950/30 dark:text-purple-300 dark:hover:bg-purple-950/50 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Marquer comme expédiée"
                        >
                          {actionLoading?.id === item.id && actionLoading?.action === 'shipped' ? (
                            <div className="h-3.5 w-3.5 border-2 border-purple-700 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Send className="h-3.5 w-3.5" />
                          )}
                          Expédier
                        </button>
                      )}
                      {item.status === 'shipped' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); void handleQuickStatusChange(item.id, 'delivered'); }}
                          disabled={actionLoading?.id === item.id}
                          className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-950/50 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Marquer comme livrée"
                        >
                          {actionLoading?.id === item.id && actionLoading?.action === 'delivered' ? (
                            <div className="h-3.5 w-3.5 border-2 border-blue-700 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Truck className="h-3.5 w-3.5" />
                          )}
                          Livrer
                        </button>
                      )}
                      {item.status === 'invoiced' && !isApporteur && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleMarkEnregistrer(item.id); }}
                          disabled={actionLoading?.id === item.id}
                          className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-300 dark:hover:bg-emerald-950/50 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Marquer comme payée"
                        >
                          {actionLoading?.id === item.id && actionLoading?.action === 'paid' ? (
                            <div className="h-3.5 w-3.5 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Check className="h-3.5 w-3.5" />
                          )}
                          Payer
                        </button>
                      )}

                      {item.status === 'delivered' && !isApporteur && (
                        <button
                          onClick={(e) => { e.stopPropagation(); void handleQuickStatusChange(item.id, 'invoiced'); }}
                          disabled={actionLoading?.id === item.id}
                          className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-950/30 dark:text-amber-300 dark:hover:bg-amber-950/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Facturer la commande"
                        >
                          {actionLoading?.id === item.id && actionLoading?.action === 'invoiced' ? (
                            <div className="h-3.5 w-3.5 border-2 border-amber-700 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <FileText className="h-3.5 w-3.5" />
                          )}
                          Facturer
                        </button>
                      )}

                      {/* Actions générales */}
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/achat/orders/${item.id}/duplicate`); }}
                        className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-950/30 dark:text-amber-300 dark:hover:bg-amber-950/50 rounded-lg transition-all"
                        title="Dupliquer"
                      >
                        <Copy className="h-3.5 w-3.5" />
                        Dupliquer
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : viewMode === 'list' ? (
          <div className="space-y-2 sm:space-y-3">
            {filtered.map((item, idx) => {
              const status = statusConfig[item.status] || statusConfig.pending;
              const StatusIcon = status.icon;
              const paymentDateValue = item.paid_at || item.enregistrer_at;
              const paymentDate = item.status === 'paid' && paymentDateValue ? formatDate(paymentDateValue) : null;
              return (
                <div
                  key={item.id}
                  onClick={() => navigate(`/achat/orders/${item.id}`)}
                  className="group rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:shadow-lg hover:border-orange-300 dark:hover:border-orange-700 transition-all duration-300 ease-out animate-in fade-in slide-in-from-left-4 overflow-hidden cursor-pointer"
                  style={{ animationDelay: `${idx * 30}ms`, animationFillMode: 'backwards' }}
                >
                  {/* Mobile list layout */}
                  <div className="sm:hidden p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-9 h-9 bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-900/30 dark:to-amber-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                        <ShoppingCart className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate">{item.order_number}</h3>
                        <p className="text-xs text-slate-600 dark:text-slate-400 truncate">{item.client?.company_name || `Client #${item.client_id}`}</p>
                      </div>
                      <div className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium shrink-0 ${(statusConfig[item.status] || statusConfig.pending).color}`}>
                        <StatusIcon className="h-3 w-3" />
                        {(statusConfig[item.status] || statusConfig.pending).label}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400 mb-2">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3 text-emerald-500" />
                        <span>{new Date(item.created_at).toLocaleDateString('fr-FR')}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Package className={`h-3 w-3 ${item.items && item.items.length > 0 ? 'text-emerald-500' : 'text-red-500'}`} />
                        <span>{item.items_count ?? item.items?.length ?? 0} article{(item.items_count ?? item.items?.length ?? 0) > 1 ? 's' : ''}</span>
                      </div>
                      {isManager && (
                        <div className={`flex items-center gap-1 ${(item.client?.admin_id || item.client?.admin) ? '' : 'italic text-slate-400'}`}>
                          <Shield className={`h-3 w-3 ${(item.client?.admin_id || item.client?.admin) ? 'text-emerald-500' : 'text-red-500'}`} />
                          <span className="truncate max-w-[100px]">{getApporteurName(item)}</span>
                        </div>
                      )}
                      {paymentDate && (
                        <div className="flex items-center gap-1">
                          <Euro className="h-3 w-3 text-emerald-500" />
                          <span>{paymentDate}</span>
                        </div>
                      )}
                    </div>
                    <div className="text-sm font-bold text-orange-600 dark:text-orange-400 mb-2">
                      {Math.round(getNetHt(item)).toLocaleString('fr-FR')} € HT
                    </div>
                    {item.notes && (
                      <div className="text-xs text-slate-500 dark:text-slate-400 mb-2 truncate">{item.notes}</div>
                    )}
                    <div className="flex flex-wrap gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); void handleViewPdf(item.id, 'bdc'); }}
                        className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-lg transition-all ${expandedOrder === item.id && pdfType === 'bdc'
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/30 dark:text-blue-300'
                          }`}
                      >
                        <FileText className="h-3 w-3" />
                        BDC
                      </button>
                      {(item.status === 'shipped' || item.status === 'delivered' || item.status === 'invoiced' || item.status === 'enregistrer' || item.status === 'paid') && (
                        <button
                          onClick={(e) => { e.stopPropagation(); void handleViewPdf(item.id, 'bdl'); }}
                          className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-lg transition-all ${expandedOrder === item.id && pdfType === 'bdl'
                            ? 'bg-purple-600 text-white shadow-md'
                            : 'bg-purple-50 text-purple-700 hover:bg-purple-100 dark:bg-purple-950/30 dark:text-purple-300'
                            }`}
                        >
                          <Truck className="h-3 w-3" />
                          BDL
                        </button>
                      )}
                      {(item.status === 'delivered' || item.status === 'invoiced' || item.status === 'paid' || item.status === 'enregistrer') && (!isApporteur || item.invoice) && (
                        <button
                          onClick={(e) => { e.stopPropagation(); void handleViewPdf(item.id, 'invoice'); }}
                          className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-lg transition-all ${expandedOrder === item.id && pdfType === 'invoice'
                            ? 'bg-green-600 text-white shadow-md'
                            : 'bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-950/30 dark:text-green-300'
                            }`}
                        >
                          <Euro className="h-3 w-3" />
                          Facture
                        </button>
                      )}
                      {isManager && item.status === 'pending' && !isApporteur && (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); void handleQuickStatusChange(item.id, 'confirmed'); }}
                            disabled={actionLoading?.id === item.id}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-300 disabled:opacity-50 transition-all"
                          >
                            {actionLoading?.id === item.id && actionLoading?.action === 'confirmed' ? <div className="h-3 w-3 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /> : <Check className="h-3 w-3" />}
                            Valider
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); void handleQuickStatusChange(item.id, 'cancelled'); }}
                            disabled={actionLoading?.id === item.id}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-lg bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-950/30 dark:text-red-300 disabled:opacity-50 transition-all"
                          >
                            {actionLoading?.id === item.id && actionLoading?.action === 'cancelled' ? <div className="h-3 w-3 border-2 border-red-700 border-t-transparent rounded-full animate-spin" /> : <XCircle className="h-3 w-3" />}
                            Refuser
                          </button>
                        </>
                      )}
                      {isManager && item.status === 'confirmed' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); void handleQuickStatusChange(item.id, 'shipped'); }}
                          disabled={actionLoading?.id === item.id}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-lg bg-purple-50 text-purple-700 hover:bg-purple-100 dark:bg-purple-950/30 dark:text-purple-300 disabled:opacity-50 transition-all"
                        >
                          {actionLoading?.id === item.id && actionLoading?.action === 'shipped' ? <div className="h-3 w-3 border-2 border-purple-700 border-t-transparent rounded-full animate-spin" /> : <Send className="h-3 w-3" />}
                          Expédier
                        </button>
                      )}
                      {isManager && item.status === 'shipped' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); void handleQuickStatusChange(item.id, 'delivered'); }}
                          disabled={actionLoading?.id === item.id}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/30 dark:text-blue-300 disabled:opacity-50 transition-all"
                        >
                          {actionLoading?.id === item.id && actionLoading?.action === 'delivered' ? <div className="h-3 w-3 border-2 border-blue-700 border-t-transparent rounded-full animate-spin" /> : <Truck className="h-3 w-3" />}
                          Livrer
                        </button>
                      )}
                      {isManager && item.status === 'delivered' && !isApporteur && (
                        <button
                          onClick={(e) => { e.stopPropagation(); void handleQuickStatusChange(item.id, 'invoiced'); }}
                          disabled={actionLoading?.id === item.id}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-950/30 dark:text-amber-300 disabled:opacity-50 transition-all"
                        >
                          {actionLoading?.id === item.id && actionLoading?.action === 'invoiced' ? <div className="h-3 w-3 border-2 border-amber-700 border-t-transparent rounded-full animate-spin" /> : <FileText className="h-3 w-3" />}
                          Facturer
                        </button>
                      )}
                      {isManager && item.status === 'invoiced' && !isApporteur && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleMarkEnregistrer(item.id); }}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-300 transition-all"
                        >
                          <Check className="h-3 w-3" />
                          Payer
                        </button>
                      )}
                      {isManager && item.status === 'cancelled' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); void deleteOrder(item.id); }}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-lg bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-950/30 dark:text-red-300 transition-all"
                        >
                          <Trash2 className="h-3 w-3" />
                          Supprimer
                        </button>
                      )}
                      {isManager && (
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/achat/orders/${item.id}/duplicate`); }}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-950/30 dark:text-amber-300 transition-all"
                        >
                          <Copy className="h-3 w-3" />
                          Dupliquer
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Desktop list layout */}
                  <div className="hidden sm:flex items-center gap-4 p-3.5">
                    {/* Icon */}
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-900/30 dark:to-amber-900/30 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                      <ShoppingCart className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                    </div>

                    {/* Main Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <h3 className="font-bold text-sm text-slate-900 dark:text-slate-100">{item.order_number}</h3>
                        <div className="flex items-start gap-3">
                          <div className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${(statusConfig[item.status] || statusConfig.pending).color}`}>
                            <StatusIcon className="h-3 w-3" />
                            {(statusConfig[item.status] || statusConfig.pending).label}
                          </div>
                          {paymentDate && (
                            <div>
                              <div className="text-xs font-semibold text-slate-900 dark:text-slate-100">
                                Date de paiement
                              </div>
                              <div className="text-xs text-slate-600 dark:text-slate-300">
                                {paymentDate}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                          <Building2 className="h-3.5 w-3.5 text-emerald-500 dark:text-emerald-400" />
                          <span className="font-medium truncate max-w-[150px]">{item.client?.company_name || `Client #${item.client_id}`}</span>
                        </div>
                        <span className="text-slate-400">•</span>
                        <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                          <Calendar className="h-3.5 w-3.5 text-emerald-500 dark:text-emerald-400" />
                          <span>{new Date(item.created_at).toLocaleDateString('fr-FR')}</span>
                        </div>
                        <span className="text-slate-400">•</span>
                        <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                          <Package className={`h-3.5 w-3.5 ${item.items && item.items.length > 0 ? 'text-emerald-500 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`} />
                          <span>{item.items_count ?? item.items?.length ?? 0} article{(item.items_count ?? item.items?.length ?? 0) > 1 ? 's' : ''}</span>
                        </div>
                        {isManager && (
                          <>
                            <span className="text-slate-400">•</span>
                            <div className={`flex items-center gap-1.5 ${(item.client?.admin_id || item.client?.admin) ? 'text-slate-600 dark:text-slate-400' : 'text-slate-400 italic'}`}>
                              <Shield className={`h-3.5 w-3.5 ${(item.client?.admin_id || item.client?.admin) ? 'text-emerald-500 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`} />
                              <span className="truncate max-w-[100px]">{getApporteurName(item)}</span>
                            </div>
                          </>
                        )}
                      </div>
                      {item.notes && (
                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 truncate">{item.notes}</div>
                      )}
                    </div>

                    {/* Amount */}
                    <div className="text-center px-4 border-l border-slate-200 dark:border-slate-700">
                      <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-0.5">
                        Montant
                      </div>
                      <div className="text-lg font-bold text-orange-600 dark:text-orange-400">
                        {Math.round(getNetHt(item)).toLocaleString('fr-FR')} € HT
                      </div>
                    </div>

                    {/* PDF Buttons + validation rapide */}
                    <div className="flex gap-1.5 px-3 border-l border-slate-200 dark:border-slate-700">
                      <button
                        onClick={(e) => { e.stopPropagation(); void handleViewPdf(item.id, 'bdc'); }}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-all ${expandedOrder === item.id && pdfType === 'bdc'
                          ? 'bg-blue-600 text-white shadow-md border border-blue-600'
                          : 'border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-600 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-950/50'
                          }`}
                        title="Voir BDC"
                      >
                        <FileText className="h-4 w-4" />
                        <span>BDC</span>
                      </button>

                      {(item.status === 'shipped' || item.status === 'delivered' || item.status === 'invoiced' || item.status === 'enregistrer' || item.status === 'paid') && (
                        <button
                          onClick={(e) => { e.stopPropagation(); void handleViewPdf(item.id, 'bdl'); }}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-all ${expandedOrder === item.id && pdfType === 'bdl'
                            ? 'bg-purple-600 text-white shadow-md border border-purple-600'
                            : 'border border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100 dark:border-purple-600 dark:bg-purple-950/30 dark:text-purple-300 dark:hover:bg-purple-950/50'
                            }`}
                          title="Voir BDL"
                        >
                          <Truck className="h-4 w-4" />
                          <span>BDL</span>
                        </button>
                      )}

                      {(item.status === 'delivered' || item.status === 'invoiced' || item.status === 'paid' || item.status === 'enregistrer') && (!isApporteur || item.invoice) && (
                        <button
                          onClick={(e) => { e.stopPropagation(); void handleViewPdf(item.id, 'invoice'); }}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-all ${expandedOrder === item.id && pdfType === 'invoice'
                            ? 'bg-green-600 text-white shadow-md border border-green-600'
                            : 'border border-green-300 bg-green-50 text-green-700 hover:bg-green-100 dark:border-green-600 dark:bg-green-950/30 dark:text-green-300 dark:hover:bg-green-950/50'
                            }`}
                          title="Voir Facture"
                        >
                          <Euro className="h-4 w-4" />
                          <span>Facture</span>
                        </button>
                      )}

                      {isManager && item.status === 'pending' && !isApporteur && (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); void handleQuickStatusChange(item.id, 'confirmed'); }}
                            disabled={actionLoading?.id === item.id}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-300 dark:hover:bg-emerald-950/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            title="Valider la commande"
                          >
                            {actionLoading?.id === item.id && actionLoading?.action === 'confirmed' ? (
                              <div className="h-4 w-4 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Check className="h-4 w-4" />
                            )}
                            <span>Valider</span>
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); void handleQuickStatusChange(item.id, 'cancelled'); }}
                            disabled={actionLoading?.id === item.id}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-950/30 dark:text-red-300 dark:hover:bg-red-950/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            title="Refuser la commande"
                          >
                            {actionLoading?.id === item.id && actionLoading?.action === 'cancelled' ? (
                              <div className="h-4 w-4 border-2 border-red-700 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <XCircle className="h-4 w-4" />
                            )}
                            <span>Refuser</span>
                          </button>
                        </>
                      )}
                    </div>

                    {/* Actions */}
                    {isManager && (
                      <div className="flex gap-1.5 pl-3 border-l border-slate-200 dark:border-slate-700">
                        {/* Quick Status Change (autres statuts) */}
                        {item.status === 'confirmed' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); void handleQuickStatusChange(item.id, 'shipped'); }}
                            disabled={actionLoading?.id === item.id}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg text-purple-700 bg-purple-50 hover:bg-purple-100 dark:bg-purple-950/30 dark:text-purple-300 dark:hover:bg-purple-950/50 transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Marquer comme expédiée"
                          >
                            {actionLoading?.id === item.id && actionLoading?.action === 'shipped' ? (
                              <div className="h-4 w-4 border-2 border-purple-700 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Send className="h-4 w-4" />
                            )}
                            <span>Expédier</span>
                          </button>
                        )}
                        {item.status === 'shipped' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); void handleQuickStatusChange(item.id, 'delivered'); }}
                            disabled={actionLoading?.id === item.id}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg text-blue-700 bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-950/50 transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Marquer comme livrée"
                          >
                            {actionLoading?.id === item.id && actionLoading?.action === 'delivered' ? (
                              <div className="h-4 w-4 border-2 border-blue-700 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Truck className="h-4 w-4" />
                            )}
                            <span>Livrer</span>
                          </button>
                        )}
                        {item.status === 'delivered' && !isApporteur && (
                          <button
                            onClick={(e) => { e.stopPropagation(); void handleQuickStatusChange(item.id, 'invoiced'); }}
                            disabled={actionLoading?.id === item.id}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg text-amber-700 bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/30 dark:text-amber-300 dark:hover:bg-amber-950/50 transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Facturer la commande"
                          >
                            {actionLoading?.id === item.id && actionLoading?.action === 'invoiced' ? (
                              <div className="h-4 w-4 border-2 border-amber-700 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <FileText className="h-4 w-4" />
                            )}
                            <span>Facturer</span>
                          </button>
                        )}
                        {item.status === 'invoiced' && !isApporteur && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleMarkEnregistrer(item.id); }}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg text-emerald-700 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-300 dark:hover:bg-emerald-950/50 transition-all hover:scale-105"
                            title="Marquer comme payée"
                          >
                            <Check className="h-4 w-4" />
                            <span>Payer</span>
                          </button>
                        )}
                        {/* General Actions */}
                        {item.status === 'cancelled' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); void deleteOrder(item.id); }}
                            className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
                            title="Supprimer"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/achat/orders/${item.id}/duplicate`); }}
                          className="p-2 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/30 rounded-lg transition-all hover:scale-110"
                          title="Dupliquer"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Visualiseur PDF */}
                </div>
              );
            })}
          </div>
        ) : viewMode === 'table' ? (
          <div className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 overflow-hidden shadow-lg">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30 border-b-2 border-orange-200 dark:border-orange-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">N° Commande</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Client</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Articles</th>
                    {isManager && <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Apporteur</th>}
                    <th className="px-4 py-3 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Statut</th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Montant</th>
                    {isManager && <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {filtered.map((item, idx) => {
                    const status = statusConfig[item.status] || statusConfig.pending;
                    const StatusIcon = status.icon;
                    const paymentDateValue = item.paid_at || item.enregistrer_at;
                    const paymentDate = item.status === 'paid' && paymentDateValue ? formatDate(paymentDateValue) : null;
                    return (
                      <tr
                        key={item.id}
                        onClick={() => navigate(`/achat/orders/${item.id}`)}
                        className="hover:bg-orange-50/50 dark:hover:bg-orange-950/20 transition-colors duration-200 animate-in fade-in slide-in-from-bottom-2 cursor-pointer"
                        style={{ animationDelay: `${idx * 20}ms`, animationFillMode: 'backwards' }}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-900/30 dark:to-amber-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                              <ShoppingCart className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                            </div>
                            <span className="font-semibold text-slate-900 dark:text-slate-100">{item.order_number}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                          {item.client?.company_name || `Client #${item.client_id}`}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                          {new Date(item.created_at).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                          {item.items_count ?? item.items?.length ?? 0} article{(item.items_count ?? item.items?.length ?? 0) > 1 ? 's' : ''}
                        </td>
                        {isManager && (
                          <td className="px-4 py-3">
                            {(item.client?.admin_id || item.client?.admin) ? (
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-full flex items-center justify-center text-xs font-bold text-blue-700 dark:text-blue-300">
                                  {getApporteurInitials(item)}
                                </div>
                                <span className="text-sm text-slate-700 dark:text-slate-300">{getApporteurName(item)}</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
                                  <Shield className="h-4 w-4 text-red-500" />
                                </div>
                                <span className="text-sm text-slate-400 italic">Non renseigné</span>
                              </div>
                            )}
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <div className="flex justify-center">
                            <div className="flex flex-col items-center gap-1">
                              <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${(statusConfig[item.status] || statusConfig.pending).color}`}>
                                <StatusIcon className="h-3 w-3" />
                                {(statusConfig[item.status] || statusConfig.pending).label}
                              </div>
                              {paymentDate && (
                                <div className="text-center">
                                  <div className="text-xs font-semibold text-slate-900 dark:text-slate-100">
                                    Date de paiement
                                  </div>
                                  <div className="text-xs text-slate-600 dark:text-slate-300">
                                    {paymentDate}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-orange-600">
                          {Math.round(getNetHt(item)).toLocaleString('fr-FR')} € HT
                        </td>
                        {isManager && (
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-1">
                              <button
                                onClick={(e) => { e.stopPropagation(); void handleViewPdf(item.id, 'bdc'); }}
                                className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg transition-all ${expandedOrder === item.id && pdfType === 'bdc'
                                  ? 'bg-blue-600 text-white shadow-md'
                                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-950/50'
                                  }`}
                                title="Voir BDC"
                              >
                                <FileText className="h-3 w-3" />
                                BDC
                              </button>

                              {(item.status === 'shipped' || item.status === 'delivered' || item.status === 'invoiced' || item.status === 'paid') && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); void handleViewPdf(item.id, 'bdl'); }}
                                  className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg transition-all ${expandedOrder === item.id && pdfType === 'bdl'
                                    ? 'bg-purple-600 text-white shadow-md'
                                    : 'bg-purple-50 text-purple-700 hover:bg-purple-100 dark:bg-purple-950/30 dark:text-purple-300 dark:hover:bg-purple-950/50'
                                    }`}
                                  title="Voir BDL"
                                >
                                  <Truck className="h-3 w-3" />
                                  BDL
                                </button>
                              )}

                              {(item.status === 'delivered' || item.status === 'invoiced' || item.status === 'paid') && (!isApporteur || item.invoice) && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); void handleViewPdf(item.id, 'invoice'); }}
                                  className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg transition-all ${expandedOrder === item.id && pdfType === 'invoice'
                                    ? 'bg-green-600 text-white shadow-md'
                                    : 'bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-950/30 dark:text-green-300 dark:hover:bg-green-950/50'
                                    }`}
                                  title="Voir Facture"
                                >
                                  <Euro className="h-3 w-3" />
                                  Facture
                                </button>
                              )}

                              {item.status === 'confirmed' && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); void handleQuickStatusChange(item.id, 'shipped'); }}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg text-purple-700 bg-purple-50 hover:bg-purple-100 dark:bg-purple-950/30 dark:text-purple-300 dark:hover:bg-purple-950/50 transition-all hover:scale-105"
                                  title="Marquer comme expédiée"
                                >
                                  <Send className="h-3.5 w-3.5" />
                                  <span>Expédier</span>
                                </button>
                              )}

                              {item.status === 'shipped' && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); void handleQuickStatusChange(item.id, 'delivered'); }}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg text-blue-700 bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-950/50 transition-all hover:scale-105"
                                  title="Marquer comme livrée"
                                >
                                  <Truck className="h-3.5 w-3.5" />
                                  <span>Livrer</span>
                                </button>
                              )}

                              {item.status === 'delivered' && !isApporteur && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); void handleQuickStatusChange(item.id, 'invoiced'); }}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg text-amber-700 bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/30 dark:text-amber-300 dark:hover:bg-amber-950/50 transition-all hover:scale-105"
                                  title="Facturer la commande"
                                >
                                  <FileText className="h-3.5 w-3.5" />
                                  <span>Facturer</span>
                                </button>
                              )}

                              {item.status === 'invoiced' && !isApporteur && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleMarkEnregistrer(item.id); }}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg text-emerald-700 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-300 dark:hover:bg-emerald-950/50 transition-all hover:scale-105"
                                  title="Marquer comme payée"
                                >
                                  <Check className="h-3.5 w-3.5" />
                                  <span>Payer</span>
                                </button>
                              )}


                              {item.status === 'pending' && !isApporteur && (
                                <>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); void handleQuickStatusChange(item.id, 'confirmed'); }}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-300 dark:hover:bg-emerald-950/50"
                                    title="Valider la commande"
                                  >
                                    <Check className="h-3.5 w-3.5" />
                                    <span>Valider</span>
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); void handleQuickStatusChange(item.id, 'cancelled'); }}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-950/30 dark:text-red-300 dark:hover:bg-red-950/50"
                                    title="Refuser la commande"
                                  >
                                    <XCircle className="h-3.5 w-3.5" />
                                    <span>Refuser</span>
                                  </button>
                                </>
                              )}

                              {item.status === 'cancelled' && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); void deleteOrder(item.id); }}
                                  className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-950/50 rounded-lg transition-all duration-200 hover:scale-110"
                                  title="Supprimer"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}

                              <button
                                onClick={(e) => { e.stopPropagation(); navigate(`/achat/orders/${item.id}/duplicate`); }}
                                className="p-2 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/30 rounded-lg transition-all hover:scale-110"
                                title="Dupliquer"
                              >
                                <Copy className="h-4 w-4" />
                              </button>

                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {expandedOrder !== null && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[90] p-0 sm:p-4"
            onClick={closePdfViewer}
          >
            <div
              className="bg-white dark:bg-slate-900 sm:rounded-2xl shadow-2xl w-full h-full sm:max-w-5xl sm:h-[85vh] border-0 sm:border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 fade-in duration-200 flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  {pdfType === 'bdc' && '📄 Bon de Commande'}
                  {pdfType === 'bdl' && '🚚 Bon de Livraison'}
                  {pdfType === 'invoice' && '🧾 Facture'}
                </h4>
                <div className="flex items-center gap-2">
                  <a
                    href={pdfUrl || '#'}
                    download
                    className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                    title="Télécharger"
                    aria-label="Télécharger le PDF"
                  >
                    <Download className="h-4 w-4" />
                  </a>
                  <button
                    onClick={closePdfViewer}
                    className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="flex-1 p-4">
                {loadingPdf ? (
                  <div className="flex items-center justify-center h-full bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-3"></div>
                      <p className="text-sm text-slate-600 dark:text-slate-400">Chargement du PDF...</p>
                    </div>
                  </div>
                ) : pdfUrl ? (
                  <iframe
                    src={pdfUrl}
                    className="w-full h-full rounded-lg border-2 border-slate-200 dark:border-slate-700 bg-white"
                    title={`PDF ${pdfType}`}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <p className="text-sm text-slate-600 dark:text-slate-400">Impossible de charger le PDF</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal Date de Paiement (ex Enregistrer) */}
        {showEnregistrerPopup && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 fade-in duration-200">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                    Enregistrer le paiement
                  </h3>
                  <button
                    onClick={() => setShowEnregistrerPopup(false)}
                    className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors"
                  >
                    <X className="h-5 w-5 text-slate-500" />
                  </button>
                </div>

                <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
                  Veuillez indiquer la date à laquelle le paiement a été reçu.
                </p>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="payment-date" className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                      Date de paiement
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        id="payment-date"
                        type="date"
                        value={enregistrerDate}
                        onChange={(e) => setEnregistrerDate(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all outline-none"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 mt-8">
                  <button
                    onClick={() => setShowEnregistrerPopup(false)}
                    className="flex-1 px-4 py-2.5 text-sm font-semibold text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-xl transition-all"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleConfirmEnregistrer}
                    className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 rounded-xl shadow-lg shadow-orange-500/20 transition-all hover:scale-[1.02] active:scale-[0.98]"
                  >
                    Confirmer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteDialog.open}
          onOpenChange={(open) => setDeleteDialog({ open, orderId: open ? deleteDialog.orderId : null })}
          title="Supprimer la commande"
          description="Êtes-vous sûr de vouloir supprimer cette commande ? Cette action est irréversible."
          confirmText="Supprimer"
          cancelText="Annuler"
          isLoading={isDeleting}
          onConfirm={handleConfirmDelete}
        />
      </div>
    </div>
  );
};

export default PurchaseOrdersTailwind;
